import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Loader2, LogOut, Mail, MessageSquare, Phone, PhoneCall, ShieldCheck, Video, MapPin, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eyebrow } from "@/components/Reveal";
import { API, SERVICE_OPTIONS, AUDIENCES } from "@/data/content";

const TOKEN_KEY = "invesmo_admin_token";
const SERVICE_MAP = Object.fromEntries(SERVICE_OPTIONS.map((s) => [s.slug, s.title]));
const AUDIENCE_MAP = Object.fromEntries(AUDIENCES.map((a) => [a.slug, a.title]));
const MEETING_LABELS = { video: "Video call", phone: "Phone", "in-person": "Camberwell office" };

const prettyDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

const prettyTime = (t) => {
  const h = parseInt(t.split(":")[0], 10);
  return `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? "AM" : "PM"}`;
};

const prettyDateTime = (iso) => new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [callbacks, setCallbacks] = useState(null);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      setToken(r.data.token);
      setUser(r.data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/auth/me`, { headers }),
      axios.get(`${API}/bookings`, { headers }),
      axios.get(`${API}/enquiries`, { headers }),
      axios.get(`${API}/callbacks`, { headers }),
    ])
      .then(([me, b, en, cb]) => {
        setUser(me.data);
        setBookings(b.data);
        setEnquiries(en.data);
        setCallbacks(cb.data);
      })
      .catch((err) => {
        if (err.response?.status === 401) logout();
        else toast.error("Could not load dashboard data.");
      });
  }, [token]);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (bookings || []).filter((b) => b.date >= today);

  return (
    <div data-testid="admin-page" className="pt-36 sm:pt-44 pb-24 min-h-[70vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <Eyebrow>Private</Eyebrow>

        {!token || !user ? (
          <div className="max-w-md">
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-ink leading-[1.05]">Invesmo <span className="font-editorial italic font-medium text-gold-600">Office.</span></h1>
            <p className="mt-4 text-ink/55 text-sm leading-relaxed">Sign in to view bookings, enquiries and callback requests. This area is private.</p>
            <form onSubmit={submitLogin} data-testid="admin-login-form" className="mt-10 rounded-3xl bg-white border border-ink/8 p-8 space-y-5">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-ink/50 mb-2.5">Email</label>
                <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="you@invesmo.com.au" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-ink/50 mb-2.5">Password</label>
                <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••••••" />
              </div>
              {error && <p data-testid="admin-login-error" className="text-sm text-red-600">{error}</p>}
              <button type="submit" data-testid="admin-login-submit" disabled={loading || !email || !password} className="w-full rounded-full bg-obsidian text-gold px-8 py-4 text-sm font-semibold tracking-wide transition-all duration-300 hover:bg-gold hover:text-obsidian disabled:opacity-40 inline-flex items-center justify-center gap-3">
                {loading && <Loader2 size={16} className="animate-spin" />}
                Sign in
              </button>
            </form>
          </div>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-ink leading-[1.05]">Good day, <span className="font-editorial italic font-medium text-gold-600">{user.name?.split(" ")[0] || "Anu"}.</span></h1>
                <p className="mt-3 text-sm text-ink/50 flex items-center gap-2"><ShieldCheck size={14} className="text-brand" /> Signed in as {user.email}</p>
              </div>
              <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-sm font-semibold text-ink hover:border-gold hover:text-gold-700 transition-all duration-300">
                <LogOut size={15} /> Sign out
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              <Stat testid="stat-upcoming" value={upcoming.length} label="upcoming meetings" />
              <Stat testid="stat-bookings" value={(bookings || []).length} label="total bookings" />
              <Stat testid="stat-enquiries" value={(enquiries || []).length} label="enquiries" />
              <Stat testid="stat-callbacks" value={(callbacks || []).filter((c) => c.status === "new").length} label="new callbacks" />
            </div>

            <Tabs defaultValue="callbacks">
              <TabsList data-testid="admin-tabs" className="rounded-full p-1.5 bg-ink/5 mb-8">
                <TabsTrigger data-testid="admin-tab-callbacks" value="callbacks" className="rounded-full px-6 py-2.5 text-sm font-semibold">Callbacks</TabsTrigger>
                <TabsTrigger data-testid="admin-tab-bookings" value="bookings" className="rounded-full px-6 py-2.5 text-sm font-semibold">Bookings</TabsTrigger>
                <TabsTrigger data-testid="admin-tab-enquiries" value="enquiries" className="rounded-full px-6 py-2.5 text-sm font-semibold">Enquiries</TabsTrigger>
              </TabsList>

              <TabsContent value="callbacks">
                {!callbacks ? (
                  <Loading />
                ) : callbacks.length === 0 ? (
                  <Empty testid="callbacks-empty" text="No callback requests yet. Share your callback link on social media to start collecting leads." />
                ) : (
                  <div className="space-y-4">
                    {callbacks.map((c, i) => (
                      <div key={c.id || i} data-testid={`callback-row-${i}`} className="rounded-3xl bg-white border border-ink/8 p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5">
                        <span className="shrink-0 w-12 h-12 rounded-2xl bg-gold-50 flex items-center justify-center">
                          <PhoneCall size={19} className="text-gold-600" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-display font-bold text-ink">{c.name}</span>
                            <span className="text-[10px] font-bold tracking-[0.15em] uppercase rounded-full bg-brand-light text-brand-dark px-2.5 py-1">{AUDIENCE_MAP[c.audience] || c.audience}</span>
                            {c.status === "new" && <span className="text-[10px] font-bold tracking-[0.15em] uppercase rounded-full bg-obsidian text-gold px-2.5 py-1">new</span>}
                          </div>
                          <div className="text-sm text-ink/55 mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                            <span className="inline-flex items-center gap-1.5"><Clock size={13} className="text-brand" />{c.preferred_time}</span>
                            <span>{prettyDateTime(c.created_at)}</span>
                            {c.source && <span className="text-ink/40">via {c.source}</span>}
                          </div>
                          {c.message && <p className="text-xs text-ink/45 mt-2 leading-relaxed">"{c.message}"</p>}
                        </div>
                        <a data-testid={`callback-call-${i}`} href={`tel:${c.phone}`} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-obsidian text-gold px-6 py-3 text-sm font-semibold transition-all duration-300 hover:bg-gold hover:text-obsidian">
                          <Phone size={14} /> {c.phone}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="bookings">
                {!bookings ? (
                  <Loading />
                ) : bookings.length === 0 ? (
                  <Empty testid="bookings-empty" text="No bookings yet. New meetings appear here (and in your inbox) the moment they're made." />
                ) : (
                  <div className="space-y-4">
                    {bookings.map((b) => (
                      <div key={b.reference} data-testid={`booking-row-${b.reference}`} className="rounded-3xl bg-white border border-ink/8 p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5">
                        <div className="shrink-0 w-14 h-14 rounded-2xl bg-gold-50 flex flex-col items-center justify-center">
                          <span className="font-display text-lg font-bold text-ink leading-none">{b.date.slice(8)}</span>
                          <span className="text-[10px] uppercase tracking-widest text-gold-700 mt-1">{new Date(`${b.date}T00:00:00`).toLocaleDateString("en-AU", { month: "short" })}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-display font-bold text-ink">{b.name}</span>
                            <span className="text-[10px] font-bold tracking-[0.15em] uppercase rounded-full bg-obsidian text-gold px-2.5 py-1">{b.reference}</span>
                            {b.date < today && <span className="text-[10px] font-semibold uppercase tracking-widest text-ink/35">past</span>}
                          </div>
                          <div className="text-sm text-ink/55 mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                            <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-brand" />{prettyDate(b.date)} · {prettyTime(b.time)}</span>
                            <span className="inline-flex items-center gap-1.5">{MEETING_LABELS[b.meeting_type] === "Video call" ? <Video size={13} className="text-brand" /> : MEETING_LABELS[b.meeting_type] === "Phone" ? <Phone size={13} className="text-brand" /> : <MapPin size={13} className="text-brand" />}{MEETING_LABELS[b.meeting_type]}</span>
                            <span>{SERVICE_MAP[b.service] || b.service}</span>
                          </div>
                          {b.notes && <p className="text-xs text-ink/45 mt-2 leading-relaxed">"{b.notes}"</p>}
                        </div>
                        <div className="shrink-0 text-sm space-y-1">
                          <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-ink/70 hover:text-brand transition-colors"><Phone size={13} />{b.phone}</a>
                          <a href={`mailto:${b.email}`} className="flex items-center gap-2 text-ink/70 hover:text-brand transition-colors"><Mail size={13} />{b.email}</a>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="enquiries">
                {!enquiries ? (
                  <Loading />
                ) : enquiries.length === 0 ? (
                  <Empty testid="enquiries-empty" text="No enquiries yet." />
                ) : (
                  <div className="space-y-4">
                    {enquiries.map((e, i) => (
                      <div key={e.id || i} data-testid={`enquiry-row-${i}`} className="rounded-3xl bg-white border border-ink/8 p-6 sm:p-7">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3">
                            <span className="w-10 h-10 rounded-2xl bg-brand-light flex items-center justify-center"><MessageSquare size={16} className="text-brand" /></span>
                            <div>
                              <div className="font-display font-bold text-ink">{e.name}</div>
                              <div className="text-xs text-ink/45 flex items-center gap-1.5"><Clock size={11} />{prettyDateTime(e.created_at)}</div>
                            </div>
                          </div>
                          <div className="text-sm flex flex-wrap gap-x-5 gap-y-1">
                            {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 text-ink/70 hover:text-brand transition-colors"><Phone size={13} />{e.phone}</a>}
                            <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 text-ink/70 hover:text-brand transition-colors"><Mail size={13} />{e.email}</a>
                          </div>
                        </div>
                        <p className="text-sm text-ink/60 leading-relaxed">{e.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-ink/10 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink/30 focus:outline-none focus:ring-2 focus:ring-gold/60 focus:border-gold transition-all duration-300";

function Stat({ value, label, testid }) {
  return (
    <div data-testid={testid} className="rounded-3xl bg-white border border-ink/8 px-5 py-6 text-center">
      <div className="font-display text-3xl sm:text-4xl font-bold text-ink">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-ink/45 mt-1.5">{label}</div>
    </div>
  );
}

function Loading() {
  return (
    <div className="flex items-center gap-3 text-ink/50 text-sm py-10" data-testid="admin-loading">
      <Loader2 size={16} className="animate-spin" /> Loading…
    </div>
  );
}

function Empty({ text, testid }) {
  return <p data-testid={testid} className="text-sm text-ink/45 py-10">{text}</p>;
}
